import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { createLoadingShimmerStyles, Avatar } from '@bloomays-lib/ui.shared';
import { CLevelMarginRankingMode, CLevelMarginRankingRow } from '../../types/primes';
import { buildCLevelAnnualDetailPath } from '../../utils/annualPrime.utils';

export type CLevelTamMarginRankingProps = {
  rows: CLevelMarginRankingRow[];
  mode: CLevelMarginRankingMode;
  selectedYear: string;
  onModeChange: (mode: CLevelMarginRankingMode) => void;
  loading?: boolean;
  loadErrorMessage?: string;
};

const CLevelTamMarginRanking = ({
  rows,
  mode,
  selectedYear,
  onModeChange,
  loading = false,
  loadErrorMessage,
}: CLevelTamMarginRankingProps) => {
  const { t } = useTranslation(['primes']);
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);

  const leftColumnRows = useMemo(() => rows.filter((_, index) => index % 2 === 0), [rows]);
  const rightColumnRows = useMemo(() => rows.filter((_, index) => index % 2 === 1), [rows]);

  return (
    <Card>
      <HeaderRow>
        <HeaderIdentity>
          <IconBadge aria-hidden="true">
            <EmojiEventsOutlinedIcon sx={{ fontSize: 18 }} />
          </IconBadge>
          <HeaderTextBlock>
            <Title>{t('clevelMarginRankingTitle')}</Title>
            <Subtitle>{t('clevelMarginRankingSubtitle')}</Subtitle>
          </HeaderTextBlock>
        </HeaderIdentity>

        <HeaderActions>
          <ModeToggle role="tablist" aria-label={t('clevelMarginRankingModeLabel')}>
            <ModeButton
              type="button"
              role="tab"
              aria-selected={mode === 'annual'}
              $active={mode === 'annual'}
              onClick={() => onModeChange('annual')}
            >
              {t('clevelMarginRankingModeAnnual')}
            </ModeButton>
            <ModeButton
              type="button"
              role="tab"
              aria-selected={mode === 'lifetime'}
              $active={mode === 'lifetime'}
              onClick={() => onModeChange('lifetime')}
            >
              {t('clevelMarginRankingModeLifetime')}
            </ModeButton>
          </ModeToggle>
          <CollapseButton type="button" onClick={() => setCollapsed((value) => !value)}>
            {collapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
            {collapsed ? t('clevelMarginRankingExpand') : t('clevelMarginRankingCollapse')}
          </CollapseButton>
        </HeaderActions>
      </HeaderRow>

      {!collapsed ? (
        <Body $loading={loading}>
          {loadErrorMessage ? <LoadErrorState role="alert">{loadErrorMessage}</LoadErrorState> : null}
          {rows.length === 0 && !loading && !loadErrorMessage ? (
            <EmptyState>{t('clevelMarginRankingEmpty')}</EmptyState>
          ) : null}
          <Columns>
            <Column>
              {leftColumnRows.map((row) => (
                <RankingRow
                  key={row.tam.email}
                  row={row}
                  selectedYear={selectedYear}
                  progressGradient={theme.palette.purple[60]}
                />
              ))}
            </Column>
            <Column>
              {rightColumnRows.map((row) => (
                <RankingRow
                  key={row.tam.email}
                  row={row}
                  selectedYear={selectedYear}
                  progressGradient={theme.palette.purple[60]}
                />
              ))}
            </Column>
          </Columns>
        </Body>
      ) : null}
    </Card>
  );
};

export default CLevelTamMarginRanking;

type RankingRowProps = {
  row: CLevelMarginRankingRow;
  selectedYear: string;
  progressGradient: string;
};

const RankingRow = ({ row, selectedYear, progressGradient }: RankingRowProps) => {
  const { t } = useTranslation(['primes']);
  const tamDisplayName = `${row.tam.firstname} ${row.tam.lastname}`.trim();

  return (
    <RowLink to={buildCLevelAnnualDetailPath(row.tam.email, selectedYear)} aria-label={t('annualActionDetail')}>
      <Rank>{row.rank}</Rank>
      <RowContent>
        <RowTop>
          <IdentityBlock>
            <Avatar size="small" alt={tamDisplayName} image={row.tam.avatarUrl} />
            <TamName>{tamDisplayName}</TamName>
          </IdentityBlock>
          <Amount>{row.displayAmount}</Amount>
        </RowTop>
        <ProgressTrack>
          <ProgressFill
            $gradient={`linear-gradient(90deg, ${progressGradient}, #5B8DEF)`}
            $percent={row.progressPercent}
          />
        </ProgressTrack>
      </RowContent>
    </RowLink>
  );
};

const Card = styled('section')(({ theme }) => ({
  borderRadius: '18px',
  backgroundColor: theme.palette.neutral.white,
  boxShadow: '0 8px 24px rgba(75, 75, 75, 0.12)',
  marginTop: theme.spacing(1),
  overflow: 'hidden',
}));

const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: '18px 22px',
  borderBottom: `1px solid ${theme.palette.neutral[15]}`,
  flexWrap: 'wrap',
}));

const HeaderIdentity = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

const IconBadge = styled('span')(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: 10,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.blue[20],
  color: theme.palette.blue[100],
  flexShrink: 0,
}));

const HeaderTextBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
}));

const Title = styled('h2')(({ theme }) => ({
  margin: 0,
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '15px',
  fontWeight: 600,
  color: theme.palette.neutral[90],
}));

const Subtitle = styled('p')(({ theme }) => ({
  margin: 0,
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '12.5px',
  color: theme.palette.neutral[60],
}));

const HeaderActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  flexWrap: 'wrap',
  marginLeft: 'auto',
}));

const ModeToggle = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: 3,
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[15],
}));

const ModeButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>(({ theme, $active }) => ({
  border: 'none',
  cursor: 'pointer',
  borderRadius: 999,
  padding: '6px 14px',
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '12.5px',
  fontWeight: 500,
  color: $active ? theme.palette.neutral[90] : theme.palette.neutral[60],
  backgroundColor: $active ? theme.palette.neutral.white : 'transparent',
  boxShadow: $active ? '0 2px 8px rgba(75, 75, 75, 0.12)' : 'none',
}));

const CollapseButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: `1px solid ${theme.palette.neutral[20]}`,
  borderRadius: 999,
  padding: '6px 12px',
  backgroundColor: theme.palette.neutral.white,
  color: theme.palette.neutral[70],
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '12px',
  cursor: 'pointer',
}));

const Body = styled('div', {
  shouldForwardProp: (prop) => prop !== '$loading',
})<{ $loading: boolean }>(({ theme, $loading }) => ({
  padding: '18px 22px 22px',
  ...createLoadingShimmerStyles({ active: $loading, contentOpacity: 0.55, speedMs: 1200, lightOpacity: 0.55 }),
  [theme.breakpoints.down('md')]: {
    padding: '16px',
  },
}));

const Columns = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2.5),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Column = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.75),
}));

const RowLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '4px 6px',
  margin: '-4px -6px',
  borderRadius: 10,
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.neutral[15],
  },
}));

const Rank = styled('span')(({ theme }) => ({
  width: 18,
  flexShrink: 0,
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '12px',
  color: theme.palette.neutral[50],
  paddingTop: 8,
}));

const RowContent = styled('div')(() => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}));

const RowTop = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

const IdentityBlock = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
}));

const TamName = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '13.5px',
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const Amount = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '13.5px',
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

const ProgressTrack = styled('div')(({ theme }) => ({
  height: 6,
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[15],
  overflow: 'hidden',
}));

const ProgressFill = styled('span', {
  shouldForwardProp: (prop) => prop !== '$gradient' && prop !== '$percent',
})<{ $gradient: string; $percent: number }>(({ $gradient, $percent }) => ({
  display: 'block',
  height: '100%',
  width: `${Math.min(100, Math.max(0, $percent))}%`,
  borderRadius: 999,
  background: $gradient,
}));

const EmptyState = styled('p')(({ theme }) => ({
  margin: 0,
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '13px',
  color: theme.palette.neutral[60],
}));

const LoadErrorState = styled('p')(({ theme }) => ({
  margin: '0 0 12px',
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '13px',
  color: theme.palette.error.main,
}));
